// Supplier responses (Lane 1331 slice 2C; redone in Lane 1395 after Ryan's QA,
// 2026-10-07: "horrendous", make it simple). One row per supplier: how they answered,
// how many parts they priced, and their notes, which open in a pop-up for review
// (unreviewed notes block an award). The offers grid lives on the Offers tab and
// feedback on Rounds and feedback; the comparison snapshot stays here. Everything shown
// is the server's; the browser only counts the notes it lists.
import React, { useCallback, useEffect, useState } from 'react';
import { formatDueAt } from '../sourcing-portal/portalView';
import ComparisonPanel from './ComparisonPanel';
import NotesReview from './NotesReview';
import useDialogFocus from './useDialogFocus';

export const SUPPLIER_FLAG_TEXT = {
  same_price_everywhere: 'Same price on every part',
  all_round_numbers: 'Every price is a whole number',
  parts_not_quoted: 'Some parts not quoted',
};

const RESPONSE_TEXT = {
  not_viewed: 'Not opened', viewed: 'Opened', draft_saved: 'Draft saved',
  submitted: 'Submitted', declined: 'Declined',
};

function NotesDialog({ supplier, onClose, ...notesProps }) {
  const box = useDialogFocus(onClose);
  return (
    <div className="srce-overlay" role="dialog" aria-modal="true" aria-label={`Notes from ${supplier.supplier_name}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="srce-modal" ref={box}>
        <div className="srce-card2-h"><span className="srce-card2-t">Notes from {supplier.supplier_name}</span>
          <button type="button" className="srce-chip-x srce-push" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
          </button></div>
        <div className="srce-modal-b">
          <NotesReview supplierId={supplier.supplier_id} {...notesProps} />
        </div>
      </div>
    </div>
  );
}

// Not reviewed and open conditions both block an award (Codex code r1 #2), so both are amber.
function notesText(list) {
  if (!list || list.length === 0) return null;
  const toReview = list.filter((n) => n.state === 'unreviewed').length;
  const open = list.filter((n) => n.state === 'open' || n.state === 'open_rule_changed').length;
  const parts = [];
  if (toReview) parts.push(`${toReview} to review`);
  if (open) parts.push(`${open} open ${open === 1 ? 'condition' : 'conditions'}`);
  if (parts.length) return { text: parts.join(', '), warn: true };
  return { text: `${list.length} reviewed`, warn: false };
}

export default function OffersTab({ api, eventId, summary, refreshKey, labelOf }) {
  const [responses, setResponses] = useState(null);
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');
  const [compareKey, setCompareKey] = useState(0);
  const canCompare = ['open', 'closed', 'awarded'].includes(summary.status);
  const currency = (summary.terms && summary.terms.currency) || 'USD';

  const load = useCallback(() => Promise.all([
    api.responses(eventId).then((r) => setResponses(r.items || [])),
    api.supplierNotes(eventId).then((r) => setNotes(r.items || [])),
  ]).then(() => setError('')).catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, summary.revision, refreshKey]);
  // A chat card (a comparison) does not move the revision; the workspace's refreshKey
  // reloads the comparison too.
  useEffect(() => { if (refreshKey) setCompareKey((k) => k + 1); }, [refreshKey]);

  const compare = async () => {
    setBuilding(true);
    setBuildError('');
    try {
      await api.buildComparison(eventId);
      setCompareKey((k) => k + 1);
    } catch (e) {
      setBuildError(e.message);
    } finally {
      setBuilding(false);
    }
  };

  const bySupplier = {};
  (notes || []).forEach((n) => { (bySupplier[n.supplier_id] = bySupplier[n.supplier_id] || []).push(n); });
  const names = Object.fromEntries((responses || []).map((r) => [String(r.supplier_id), r.supplier_name]));

  return (
    <div className="srce-stack">
      {error && <div className="srce-error" role="alert">{error}</div>}
      {buildError && <div className="srce-error" role="alert">{buildError}</div>}
      <section className="srce-card2">
        <div className="srce-card2-h">
          <span className="srce-card2-t">Supplier responses</span>
          {canCompare && (
            <span className="srce-push">
              <button type="button" className="srce-btn" disabled={building} onClick={compare}>
                {building ? 'Comparing…' : 'Compare bids'}</button>
            </span>
          )}
        </div>
        {!responses && !error && <div className="ll-loading">Loading responses…</div>}
        {responses && responses.length === 0 && <div className="srce-card2-f srce-mut">No supplier is invited yet.</div>}
        {responses && responses.length > 0 && (
          <div className="srce-scroll">
            <table className="srce-sc-tbl" data-testid="supplier-responses">
              <thead>
                <tr><th>Supplier</th><th>Response</th><th className="srce-num">Priced</th><th className="srce-num">No bid</th>
                  <th className="srce-num">Not quoted</th><th>Notes</th><th>Check</th><th>Last offer</th></tr>
              </thead>
              <tbody>
                {responses.map((r) => {
                  const notQuoted = Math.max(0, (r.parts_total || 0) - (r.parts_priced || 0) - (r.parts_no_bid || 0));
                  const n = notesText(bySupplier[r.supplier_id]);
                  const flags = (r.flags || []).map((f) => SUPPLIER_FLAG_TEXT[f] || f).join('. ');
                  return (
                    <tr key={r.supplier_id}>
                      <td title={r.supplier_name}>{r.supplier_name}</td>
                      <td>{RESPONSE_TEXT[r.response] || r.response}</td>
                      <td className="srce-num">{Number(r.parts_priced || 0).toLocaleString()}</td>
                      <td className="srce-num">{Number(r.parts_no_bid || 0).toLocaleString()}</td>
                      <td className="srce-num">{notQuoted.toLocaleString()}</td>
                      <td>{n && (
                        <button type="button" className={`ll-clr${n.warn ? ' srce-warn-text' : ''}`} onClick={() => setOpen(r)}>{n.text}</button>
                      )}</td>
                      <td className="srce-warn-text" title={flags}>{flags}</td>
                      <td>{r.last_offer_at ? formatDueAt(r.last_offer_at) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <ComparisonPanel api={api} eventId={eventId} refreshKey={compareKey} onRebuild={compare} building={building} />
      {open && (
        <NotesDialog supplier={open} onClose={() => setOpen(null)} api={api} eventId={eventId}
          refreshKey={refreshKey} currency={currency} labelOf={labelOf} names={names} onReviewed={load} />
      )}
    </div>
  );
}
